import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import jsPDF from "jspdf";
import moment from "moment-timezone";
import { Brain, Download, ChevronDown, AlertTriangle, TrendingUp } from "lucide-react";
import { useDashTheme } from "../hooks/useDashTheme";
import { Modal } from "./forms";
import { Button, Badge } from "./primitives";
import { EmptyState } from "./DataTable";
import MessageRenderer from "../../../components/MessageRenderer/MessageRenderer";
import { SERVER_URL } from "../../../utils/serverConfig";

// Parse the streamed text into per-user blocks (verbatim from BrainOps).
function parsePerUserFromText(text) {
  const users = [];
  const userBlocks = (text || "").split(/\*\*USER:\s*(.+?)\*\*/gi);
  for (let i = 1; i < userBlocks.length; i += 2) {
    const userNameMatch = userBlocks[i];
    const userContent = userBlocks[i + 1] || "";
    const issuesMatch = userContent.match(/\*\*SECTION 2: ISSUES DETECTED\*\*([\s\S]*?)(?=\*\*SECTION 3:|$)/i);
    const improvementsMatch = userContent.match(/\*\*SECTION 3: PERFORMANCE IMPROVEMENTS\*\*([\s\S]*?)(?=\*\*USER:|---|$)/i);
    if (userNameMatch) {
      const nameParts = userNameMatch.trim().split("(");
      const fullName = nameParts[0].trim();
      const position = nameParts[1] ? nameParts[1].replace(")", "").trim() : "";
      users.push({
        userName: fullName,
        position,
        issuesDetected: issuesMatch ? issuesMatch[1].trim() : "",
        performanceImprovements: improvementsMatch ? improvementsMatch[1].trim() : "",
      });
    }
  }
  return users;
}

/**
 * OverallIntelligenceModal
 * ------------------------
 * Org-wide intelligence report (all accessible users) — restores the legacy
 * BrainOps "Overall Intelligence" flow in the V2 design.
 *  - streams POST /api/v1/org-intelligence/overall (same endpoint/body)
 *  - parses per-user blocks (verbatim parsePerUserFromText)
 *  - multi-page jsPDF export (verbatim builder)
 * No backend changes.
 *
 * Props: open, onClose, dateRange { startDate, endDate }, onLimitReached
 */
export function OverallIntelligenceModal({ open, onClose, dateRange, onLimitReached }) {
  const t = useDashTheme();
  const [generating, setGenerating] = React.useState(false);
  const [streamedText, setStreamedText] = React.useState("");
  const [users, setUsers] = React.useState([]);
  const [expanded, setExpanded] = React.useState({}); // per-user collapse
  const abortRef = React.useRef(null);

  const run = React.useCallback(async () => {
    if (!dateRange?.startDate || !dateRange?.endDate) { toast.error("Please select a date range"); return; }
    setGenerating(true);
    setUsers([]);
    setStreamedText("");
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${SERVER_URL}/api/v1/org-intelligence/overall`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ startDate: dateRange.startDate, endDate: dateRange.endDate, forceRegenerate: false }),
        signal: abortRef.current.signal,
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        if (errData?.error?.code === "AI_LIMIT_REACHED") { onLimitReached?.(); setGenerating(false); return; }
        throw new Error(`Failed to generate overall intelligence: ${res.statusText}`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let fullText = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const data = JSON.parse(line.substring(6));
            if (data.type === "content") { fullText += data.text; setStreamedText(fullText); }
            else if (data.type === "done") {
              if (data.users && Array.isArray(data.users) && data.users.length > 0) {
                setUsers(data.users);
                toast.success(`Generated intelligence for ${data.users.length} user(s)`);
              } else {
                const parsed = parsePerUserFromText(fullText);
                setUsers(parsed);
                if (parsed.length > 0) toast.success(`Generated intelligence for ${parsed.length} user(s)`);
              }
              setGenerating(false);
            } else if (data.type === "error") { toast.error(data.message); setGenerating(false); }
          } catch (_) { /* ignore parse errors */ }
        }
      }
      setGenerating(false);
    } catch (err) {
      if (err.name !== "AbortError") { toast.error(`Failed to generate overall intelligence: ${err.message}`); }
      setGenerating(false);
    }
  }, [dateRange, onLimitReached]);

  // Auto-run when the modal opens.
  React.useEffect(() => {
    if (open) run();
    return () => { if (abortRef.current) abortRef.current.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Multi-page PDF export (verbatim builder from BrainOps.downloadOverallPDF).
  const downloadPDF = React.useCallback(() => {
    if (users.length === 0) { toast.error("No data to download"); return; }
    try {
      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      let yPosition = margin;

      pdf.setFontSize(18);
      pdf.setFont("helvetica", "bold");
      pdf.text("Overall Organizational Intelligence Report", margin, yPosition);
      yPosition += 15;

      pdf.setFontSize(12);
      pdf.setFont("helvetica", "normal");
      const dateText = dateRange.startDate === dateRange.endDate
        ? `Date: ${moment(dateRange.startDate).format("MMMM D, YYYY")}`
        : `Date Range: ${moment(dateRange.startDate).format("MMM D, YYYY")} - ${moment(dateRange.endDate).format("MMM D, YYYY")}`;
      pdf.text(dateText, margin, yPosition);
      yPosition += 10;
      pdf.text(`Generated on: ${moment().format("MMMM D, YYYY HH:mm")}`, margin, yPosition);
      yPosition += 20;

      users.forEach((user, index) => {
        if (yPosition > pageHeight - 60) { pdf.addPage(); yPosition = margin; }
        pdf.setFontSize(14);
        pdf.setFont("helvetica", "bold");
        const userTitle = `${index + 1}. ${user.userName}${user.position ? ` (${user.position})` : ""}`;
        pdf.text(userTitle, margin, yPosition);
        yPosition += 10;

        if (user.issuesDetected && user.issuesDetected.trim()) {
          pdf.setFontSize(12);
          pdf.setFont("helvetica", "bold");
          pdf.text("Issues Detected:", margin, yPosition);
          yPosition += 8;
          pdf.setFont("helvetica", "normal");
          const issuesLines = pdf.splitTextToSize(user.issuesDetected, pageWidth - 2 * margin);
          pdf.text(issuesLines, margin, yPosition);
          yPosition += issuesLines.length * 5 + 10;
        }

        if (user.performanceImprovements && user.performanceImprovements.trim()) {
          if (yPosition > pageHeight - 40) { pdf.addPage(); yPosition = margin; }
          pdf.setFontSize(12);
          pdf.setFont("helvetica", "bold");
          pdf.text("Performance Improvements:", margin, yPosition);
          yPosition += 8;
          pdf.setFont("helvetica", "normal");
          const improvementsLines = pdf.splitTextToSize(user.performanceImprovements, pageWidth - 2 * margin);
          pdf.text(improvementsLines, margin, yPosition);
          yPosition += improvementsLines.length * 5 + 15;
        }
        yPosition += 10;
      });

      pdf.save(`Overall_Intelligence_${moment().format("YYYY-MM-DD_HH-mm")}.pdf`);
      toast.success("PDF downloaded successfully!");
    } catch (error) {
      toast.error("Failed to generate PDF");
    }
  }, [users, dateRange]);

  const toggle = (i) => setExpanded((e) => ({ ...e, [i]: !e[i] }));

  return (
    <Modal
      open={open} onClose={onClose} width={760}
      title="Overall organizational intelligence" icon={<Brain size={18} />}
      footer={<>
        <Button variant="secondary" onClick={onClose}>Close</Button>
        <Button variant="primary" leftIcon={<Download size={15} />} onClick={downloadPDF} disabled={users.length === 0 || generating}>Download PDF</Button>
      </>}
    >
      {generating && users.length === 0 ? (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: `${t.s[8]}px ${t.s[5]}px`, gap: t.s[3] }}>
          <motion.span animate={{ scale: [1, 1.15, 1], opacity: [0.7, 1, 0.7] }} transition={{ duration: 1.4, repeat: Infinity }}
            style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 52, height: 52, borderRadius: 16, background: t.c.primaryGradient, color: "#fff", boxShadow: `0 8px 24px ${t.c.glow}` }}>
            <Brain size={24} />
          </motion.span>
          <div style={{ fontSize: t.font.size.lg, fontWeight: 600, color: t.c.text }}>Analyzing all team members…</div>
          <div style={{ fontSize: t.font.size.sm, color: t.c.textTertiary }}>This may take a moment for larger teams</div>
          {streamedText && <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, maxWidth: 480, textAlign: "center" }}>{streamedText.slice(-160)}</div>}
        </div>
      ) : users.length === 0 ? (
        <EmptyState icon={<Brain size={22} />} title="No intelligence data" description="No activity found for the selected period." />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: t.s[3], maxHeight: "60vh", overflowY: "auto" }}>
          <div style={{ padding: `${t.s[2]}px ${t.s[3]}px`, borderRadius: t.r.md, background: t.c.primarySoft, color: t.c.primary, fontSize: t.font.size.sm, fontWeight: 600 }}>
            {users.length} team member{users.length === 1 ? "" : "s"} analyzed
          </div>
          {users.map((u, i) => {
            const hasIssues = u.issuesDetected && u.issuesDetected.trim();
            const hasImpr = u.performanceImprovements && u.performanceImprovements.trim();
            const isOpen = !!expanded[i];
            return (
              <div key={i} style={{ borderRadius: t.r.md, border: `1px solid ${t.c.border}`, overflow: "hidden", background: t.c.surface }}>
                <button onClick={() => toggle(i)}
                  style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: t.s[3], background: "transparent", border: "none", cursor: "pointer", fontFamily: t.font.family }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                    <span style={{ fontSize: t.font.size.md, fontWeight: 600, color: t.c.text }}>{i + 1}. {u.userName}</span>
                    {u.position && <Badge tone="neutral">{u.position}</Badge>}
                  </span>
                  <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    {hasIssues && <Badge tone="negative">Issues</Badge>}
                    {hasImpr && <Badge tone="positive">Improvements</Badge>}
                    <ChevronDown size={16} style={{ color: t.c.textTertiary, transform: isOpen ? "rotate(180deg)" : "none", transition: "transform 160ms" }} />
                  </span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} style={{ overflow: "hidden" }}>
                      <div style={{ padding: t.s[3], paddingTop: 0, display: "flex", flexDirection: "column", gap: t.s[3] }}>
                        {hasIssues && (
                          <div style={{ padding: t.s[3], borderRadius: t.r.md, background: t.status.negativeSoft, border: `1px solid ${t.c.border}` }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6, fontWeight: 600, color: t.status.negative }}><AlertTriangle size={14} /> Issues Detected</div>
                            <div className="dashv2-ai-content" style={{ fontSize: t.font.size.sm, color: t.c.textSecondary, lineHeight: 1.6 }}>
                              <MessageRenderer text={u.issuesDetected} isDark={t.isDark} />
                            </div>
                          </div>
                        )}
                        {hasImpr && (
                          <div style={{ padding: t.s[3], borderRadius: t.r.md, background: t.status.positiveSoft, border: `1px solid ${t.c.border}` }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6, fontWeight: 600, color: t.status.positive }}><TrendingUp size={14} /> Performance Improvements</div>
                            <div className="dashv2-ai-content" style={{ fontSize: t.font.size.sm, color: t.c.textSecondary, lineHeight: 1.6 }}>
                              <MessageRenderer text={u.performanceImprovements} isDark={t.isDark} />
                            </div>
                          </div>
                        )}
                        {!hasIssues && !hasImpr && (
                          <div style={{ padding: t.s[3], borderRadius: t.r.md, background: t.c.surfaceMuted, fontSize: t.font.size.sm, fontStyle: "italic", color: t.c.textTertiary }}>
                            No specific intelligence data available for this user.
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}

export default OverallIntelligenceModal;
